/**
 * Runtime for the ported founder-story pages.
 *
 * These pages were built in Replo and are now served as static markup from the
 * theme. Replo's own runtime bundle (replocdn.com/w/<project>/<page>.min.js)
 * drove the carousels, the countdown and the CTAs; this file replaces it.
 *
 * The ported markup keeps Replo's data attributes, so everything is found by
 * contract rather than by class name:
 *   [data-replo-carousel]                     carousel root
 *   [data-replo-part="slide-track"]           the translated element
 *   [data-replo-part="slide"]                 individual slides
 *   [data-replo-part="nav-button"][data-direction="previous"|"next"]
 *   [aria-label^="Go to slide"]               pagination dots
 *
 * Config comes from the section as JSON on [data-ns-founder-story].
 */
(function () {
  'use strict';

  var CTA_SELECTOR = 'button, a[role="button"], [role="button"]';

  function classList(el) {
    return (el.getAttribute('class') || '').split(/\s+/).filter(Boolean);
  }

  /**
   * Replo marks the active dot with an extra modifier class that differs per
   * carousel, so derive it: whatever the initially-current dot has that its
   * siblings do not.
   */
  function activeModifier(dots) {
    if (!dots.length) return [];
    var shared = null;
    dots.forEach(function (d) {
      var cls = classList(d);
      shared = shared === null ? cls : shared.filter(function (c) { return cls.indexOf(c) !== -1; });
    });
    var current = null;
    for (var i = 0; i < dots.length; i++) {
      if (dots[i].getAttribute('aria-current') === 'true') { current = dots[i]; break; }
    }
    if (!current) return [];
    return classList(current).filter(function (c) { return shared.indexOf(c) === -1; });
  }

  function initCarousel(root) {
    var track = root.querySelector('[data-replo-part="slide-track"]');
    if (!track) return;

    var viewport = track.parentElement;
    var slides = Array.prototype.filter.call(track.children, function (n) {
      return n.getAttribute && n.getAttribute('data-replo-part') === 'slide';
    });
    if (slides.length < 2) return;

    var dots = Array.prototype.slice.call(root.querySelectorAll('[aria-label^="Go to slide"]'));
    var prev = root.querySelector('[data-replo-part="nav-button"][data-direction="previous"]');
    var next = root.querySelector('[data-replo-part="nav-button"][data-direction="next"]');
    var modifier = activeModifier(dots);
    var count = dots.length || slides.length;
    var index = 0;

    track.style.transitionProperty = 'transform';
    track.style.transitionDuration = '350ms';
    track.style.transitionTimingFunction = 'ease';

    // Replo ships TWO carousel mechanisms and they need different handling:
    //   - stacked: every slide sits at the same x and is cross-faded via
    //     opacity; the track never moves (the review sliders)
    //   - sliding: slides sit side by side and the track is translated
    // Detect from the rendered layout rather than hardcoding per carousel, so
    // this stays correct across the page's three breakpoint tiers.
    function step() {
      var a = slides[0].getBoundingClientRect();
      var b = slides[1].getBoundingClientRect();
      return b.left - a.left;
    }

    function isStacked() {
      return step() <= 1;
    }

    // Replo published an explicit inline opacity on the inactive slides of the
    // carousels it fades, and none on the ones where every slide stays visible
    // at once. That published value is the reliable signal for which behaviour
    // this carousel wants — without honouring it, a correctly-positioned slide
    // renders blank because it keeps its opacity:0 from the markup.
    var fadesInactive = slides.some(function (s) {
      return s.style && s.style.opacity !== '' && s.style.opacity !== '1';
    });

    function maxOffset() {
      if (!viewport) return Infinity;
      return Math.max(0, track.scrollWidth - viewport.clientWidth);
    }

    function render() {
      if (!isStacked()) {
        var offset = Math.min(index * step(), maxOffset());
        track.style.transform = 'translate3d(' + -offset + 'px, 0px, 0px)';
      }
      if (fadesInactive) {
        slides.forEach(function (s, i) {
          s.style.transition = 'opacity 300ms ease';
          s.style.opacity = i === index ? '1' : '0';
          s.style.pointerEvents = i === index ? '' : 'none';
        });
      }

      dots.forEach(function (d, i) {
        d.setAttribute('aria-current', i === index ? 'true' : 'false');
        modifier.forEach(function (c) { d.classList[i === index ? 'add' : 'remove'](c); });
      });
      slides.forEach(function (s, i) {
        s.setAttribute('data-is-active', i === index ? 'true' : 'false');
      });
      if (prev) prev.disabled = index <= 0;
      if (next) next.disabled = index >= count - 1;
    }

    function goTo(i) {
      index = Math.max(0, Math.min(count - 1, i));
      render();
    }

    if (prev) prev.addEventListener('click', function () { goTo(index - 1); });
    if (next) next.addEventListener('click', function () { goTo(index + 1); });
    dots.forEach(function (d, i) {
      d.addEventListener('click', function () { goTo(i); });
      d.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goTo(i); }
      });
    });

    // Slide pitch is viewport-dependent; re-measure rather than assume.
    var resizeTimer = null;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(render, 150);
    });

    render();
  }

  /**
   * The countdown's starting value is rendered into the markup and ticked down
   * from there — the same thing Replo's runtime did. It is not an absolute
   * deadline: the published value is what every visitor starts from.
   */
  function initCountdown(root) {
    var cells = {};
    var labels = root.querySelectorAll('p, span, div');
    Array.prototype.forEach.call(labels, function (el) {
      var text = (el.textContent || '').trim().toLowerCase();
      if (text !== 'hr' && text !== 'mins' && text !== 'sec') return;
      if (el.children.length) return;
      // walk out to the cell that sits beside the number
      var node = el;
      for (var i = 0; i < 4 && node; i++) {
        var sib = node.previousElementSibling;
        if (sib && /^\d+$/.test((sib.textContent || '').trim())) {
          if (!cells[text]) cells[text] = sib;
          return;
        }
        node = node.parentElement;
      }
    });

    if (!cells.hr || !cells.mins || !cells.sec) return;

    function read(el) { return parseInt((el.textContent || '0').trim(), 10) || 0; }
    var total = read(cells.hr) * 3600 + read(cells.mins) * 60 + read(cells.sec);

    function pad(n) { return n < 10 ? '0' + n : String(n); }
    function paint() {
      var h = Math.floor(total / 3600);
      var m = Math.floor((total % 3600) / 60);
      var s = total % 60;
      // write into the leaf so the surrounding markup and styling are untouched
      setText(cells.hr, pad(h));
      setText(cells.mins, pad(m));
      setText(cells.sec, pad(s));
    }
    function setText(cell, value) {
      var leaf = cell;
      while (leaf.firstElementChild) leaf = leaf.firstElementChild;
      leaf.textContent = value;
    }

    paint();
    setInterval(function () {
      if (total <= 0) return;
      total -= 1;
      paint();
    }, 1000);
  }

  /**
   * Replo drove add-to-cart over AJAX from its runtime. These CTAs now go to the
   * product page instead.
   */
  function initCtas(root, cfg) {
    if (!cfg.productUrl) return;
    var wanted = (cfg.ctaLabels || []).map(function (t) { return t.toLowerCase(); });
    if (!wanted.length) return;

    Array.prototype.forEach.call(root.querySelectorAll(CTA_SELECTOR), function (el) {
      var text = (el.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
      if (wanted.indexOf(text) === -1) return;
      el.style.cursor = 'pointer';
      el.addEventListener('click', function (e) {
        e.preventDefault();
        window.location.assign(cfg.productUrl);
      });
    });
  }

  /**
   * These videos ship with a poster and no controls, so Replo's runtime supplied
   * the play affordance. Click toggles play/pause.
   */
  function initVideos(root) {
    Array.prototype.forEach.call(root.querySelectorAll('video'), function (v) {
      if (v.hasAttribute('controls') || v.hasAttribute('autoplay')) return;
      v.setAttribute('playsinline', '');

      // Replo lays a poster image and an SVG play button OVER the video, so a
      // listener on the <video> itself never receives the click — the overlay
      // swallows it. Bind to the nearest ancestor that holds the play icon.
      var host = v.parentElement;
      for (var i = 0; i < 5 && host && !host.querySelector('svg'); i++) {
        host = host.parentElement;
      }
      if (!host) host = v.parentElement;
      if (!host) return;

      var icons = Array.prototype.slice.call(host.querySelectorAll('svg'));
      var posters = Array.prototype.filter.call(
        v.parentElement ? v.parentElement.children : [],
        function (n) { return n !== v && n.tagName === 'IMG'; }
      );
      var overlay = icons.concat(posters);

      function showOverlay(show) {
        overlay.forEach(function (o) {
          o.style.opacity = show ? '' : '0';
          o.style.pointerEvents = show ? '' : 'none';
        });
      }

      host.style.cursor = 'pointer';
      host.addEventListener('click', function (e) {
        // never hijack a real link that happens to sit over the video
        if (e.target && e.target.closest && e.target.closest('a[href]')) return;
        if (v.paused) { v.play().catch(function () {}); } else { v.pause(); }
      });
      v.addEventListener('play', function () { showOverlay(false); });
      v.addEventListener('pause', function () { showOverlay(true); });
    });
  }

  /**
   * Replo's runtime injected third-party video embeds at page load — they are
   * not in the published markup, so the container renders as an empty gap
   * without this. Kept lazy: the iframe is only created when the block scrolls
   * into view, so the page makes no YouTube request until it is needed.
   */
  function initEmbeds(root, cfg) {
    (cfg.videoEmbeds || []).forEach(function (embed) {
      var mount = root.querySelector('[data-rid="' + embed.rid + '"]');
      if (!mount || mount.querySelector('iframe')) return;

      // The published markup already contains the sizing wrapper Replo used
      // (aspect-ratio / --rhe). The iframe belongs INSIDE it, in the same
      // width:100%;height:100% box Replo nested it in — appending it to the
      // mount instead leaves the wrapper empty and the iframe unsized.
      var wrapper = mount.firstElementChild || mount;
      var box = document.createElement('div');
      box.style.cssText = 'width: 100%; height: 100%;';

      var f = document.createElement('iframe');
      f.setAttribute('frameborder', '0');
      f.setAttribute('allowfullscreen', '');
      f.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share');
      f.src = embed.src;
      f.title = embed.title || '';
      f.width = '100%';
      f.height = '100%';

      box.appendChild(f);
      wrapper.appendChild(box);
    });
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-ns-founder-story]'), function (root) {
      var cfg = {};
      try { cfg = JSON.parse(root.getAttribute('data-ns-founder-story') || '{}'); } catch (e) { cfg = {}; }
      Array.prototype.forEach.call(root.querySelectorAll('[data-replo-carousel]'), initCarousel);
      initCountdown(root);
      initCtas(root, cfg);
      initVideos(root);
      initEmbeds(root, cfg);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
